import type { SVGProps } from 'react'

/**
 * The "fresh one" marker, lifted from the marketing site's header.
 *
 * stampede.ai's nav flags its newest destination with a single small
 * brand-gradient dot beside the label — `SECONDARY_LINKS` / `HeaderV5` in
 * apps/wir — rather than an icon or a second row of navigation. It reads as
 * "something landed here" instantly and spends almost no pixels doing it, so
 * the sidebar's "What's new" row uses the same mark.
 *
 * A dot, not a glyph, on purpose: in a sidebar every other row is a line icon
 * standing for a SECTION. A "what's new" row is not a section, it is news, and
 * borrowing a pictogram for it (a sparkle, a newspaper) reads as decoration —
 * `Sparkles` in particular already means "AI" across the product.
 *
 * Lives with the feature-announcement block rather than in a host app because
 * it is the same announcement, in its smallest form: the band says what is new,
 * this says only that something is. Shared components belong in the package.
 *
 * The gradient is a `<linearGradient>` rather than `var(--brand-gradient)`
 * because an SVG `fill` cannot take a CSS gradient function. Raw hex in the
 * stops is the one place the design law allows it (Safari will not resolve
 * `var()` inside a stop); these equal --brand-hot / --brand-warm / --brand-glow.
 *
 * Sized to the 6px dot the header draws, centred in the 24px icon box the
 * sidebar gives every row, so it sits on the same optical centre as its
 * neighbours. It breathes on the shared `.gpf-live` cycle so the row reads as
 * live without ever becoming a blinking distraction.
 */
export function NewDotIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      {...props}
    >
      <defs>
        <linearGradient id="nav-new-dot" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#ff2d87" />
          <stop offset="55%" stopColor="#ff6b4a" />
          <stop offset="100%" stopColor="#ffa234" />
        </linearGradient>
      </defs>
      {/* The house "live" breath — a slow opacity fade, never a ping. Same
          2.8s cycle the fan-in's live dot uses, so every pulsing mark in the
          product beats together. Frozen under prefers-reduced-motion by the
          rule that ships with it. Applied to the circle rather than the <svg>
          so it cannot be clobbered by a className the sidebar passes in. */}
      <circle
        className="gpf-live"
        cx="12"
        cy="12"
        r="4.5"
        fill="url(#nav-new-dot)"
      />
    </svg>
  )
}
